import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { SelectableCard, SelectableCardIndicator } from '@/components/data-display/selectable-card'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { DrawerFormLayout, DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { FormSection } from '@/components/forms/form-section'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { Drawer } from '@/components/overlays/drawer'
import {
  DrawerStepProgress,
  DrawerStepTitle,
  DrawerTabs,
  DrawerWizardFooter,
} from '@/components/overlays/drawer-wizard'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import {
  listTencentAdpCredentialOptions,
  tencentAdpCredentialOptionsQueryKey,
  type TencentAdpCredentialOption,
} from '@/features/admin/adp-credentials/api'
import {
  buildAgentToolBindingsPayload,
  getAgentToolCatalog,
  getSelfAgentToolBindings,
  updateSelfAgentToolBindings,
  type AgentToolOption,
} from '@/features/admin/agent-tools/api'
import {
  AgentToolBindingCheckboxes,
  AgentToolDraftSelector,
} from '@/features/admin/agent-tools/tool-binding-checkboxes'
import { listRegisteredModels, type RegisteredModel } from '@/features/admin/model-provider/api'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { ApiError } from '@/lib/api/http-client'
import { cn } from '@/lib/cn'

import {
  batchBindAdminAppMembers,
  bindAdminAppDepartment,
  createAdminApp,
  createOrUpdateAdminAppMember,
  deleteAdminApp,
  getAdminAppOidcConfiguration,
  getAdminAppNameAvailability,
  getAdminAppDetail,
  getAdminAppTypeConfig,
  listAdminAppTransitions,
  listAdminAppTagOptions,
  transitionAdminAppStatus,
  updateAdminApp,
  updateAdminAppAvatar,
  updateAdminAppOidcConfiguration,
  validateTencentAdpAppConfig,
  type AdminApp,
  type AdminAppTag,
  type AdminAppTransitionAction,
  type AdminAppType,
  type AdminAppTypeConfigField,
  type AppConfigValue,
  type AppOidcConfiguration,
  type TencentAdpValidationResult,
} from './api'
import { AdminAppIconPicker } from './app-icon-picker'
import { AdminAppLifecycleProgress, AdminAppTransitionHistory } from './lifecycle-display'
import { formatDateTime } from './format'
import { EmbedOidcBindingSection } from './oidc-binding-section'
import { emptyEmbedOidcFormValues, type EmbedOidcFormValues } from './oidc-form-values'
import { AdminAppNameCell } from './shared'
import {
  AdminAppMembersDrawer,
  type AdminAppMemberScopeDraft,
  type AdminAppMemberScopeDrafts,
} from './members-drawer'

type DrawerMode = 'create' | 'edit'

function mergeAdminAppMemberScopeDrafts(
  drafts: AdminAppMemberScopeDraft[],
): AdminAppMemberScopeDraft {
  const accountIds = new Set<string>()
  const excludedAccountIds = new Set<string>()
  const orgUnitIds = new Set<string>()
  let effectiveMemberCount = 0

  drafts.forEach((draft) => {
    draft.accountIds.forEach((id) => accountIds.add(id))
    draft.excludedAccountIds.forEach((id) => excludedAccountIds.add(id))
    draft.orgUnitIds.forEach((id) => orgUnitIds.add(id))
    effectiveMemberCount += draft.effectiveMemberCount
  })

  return {
    accountIds: Array.from(accountIds),
    effectiveMemberCount,
    excludedAccountIds: Array.from(excludedAccountIds),
    orgUnitIds: Array.from(orgUnitIds),
  }
}

interface AdminAppFormDrawerProps {
  appId?: string | null
  appTypes: AdminAppType[]
  formSession?: number
  mode: DrawerMode
  onOpenChange: (open: boolean) => void
  open: boolean
}

interface FormValues {
  appType: string
  avatar: string | null
  config: Record<string, AppConfigValue>
  description: string
  distributionMode: 'automatic' | 'manual'
  name: string
  oidc: EmbedOidcFormValues
  newTags: string[]
  tagIds: string[]
  version: string
}

type FormErrors = Partial<Record<keyof FormValues, string>> & Record<string, string | undefined>

type CreateWizardStep = 'basic' | 'type' | 'config' | 'distribution' | 'lifecycle'
type EditDrawerTab = 'basic' | 'config' | 'lifecycle'
type CreationTargetStatus = 'draft' | 'testing' | 'pending_publish' | 'active'

interface AppFormMutationResult {
  app: AdminApp
  lifecycleWarning?: string
}

interface RegisteredModelSelectState {
  isError: boolean
  isLoading: boolean
  models: RegisteredModel[]
}

interface TencentAdpCredentialSelectState {
  credentials: TencentAdpCredentialOption[]
  isError: boolean
  isLoading: boolean
  refresh: () => void
}

const createWizardSteps = ['basic', 'type', 'config', 'distribution', 'lifecycle'] as const
const editDrawerTabs = ['basic', 'config', 'lifecycle'] as const
const creationTargetStatuses = ['draft', 'testing', 'pending_publish', 'active'] as const
const versionPattern = /^$|^\d+\.\d+\.\d+$/
const urlTemplateVariablePattern = /\{\{\w+\}\}/g

const creationTransitionActions: Record<CreationTargetStatus, AdminAppTransitionAction[]> = {
  active: ['approve_for_testing', 'request_publish', 'publish'],
  draft: [],
  pending_publish: ['approve_for_testing', 'request_publish'],
  testing: ['approve_for_testing'],
}

const emptyFormValues: FormValues = {
  appType: '',
  avatar: null,
  config: {},
  description: '',
  distributionMode: 'manual',
  name: '',
  oidc: emptyEmbedOidcFormValues,
  newTags: [],
  tagIds: [],
  version: '',
}

function configValueToString(value: AppConfigValue | undefined) {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

function configValueToBoolean(value: AppConfigValue | undefined) {
  return value === true || value === 'true' || value === 'True'
}

function normalizeInitialConfig(config: Record<string, AppConfigValue>) {
  const nextConfig = { ...config }

  for (const key of Object.keys(config)) {
    if (!key.startsWith('masked_')) {
      continue
    }

    nextConfig[key.slice('masked_'.length)] = ''
  }

  return nextConfig
}

function maskedFieldValue(config: Record<string, AppConfigValue>, field: AdminAppTypeConfigField) {
  return configValueToString(config[`masked_${field.key}`])
}

function submitConfig(config: Record<string, AppConfigValue>) {
  return Object.fromEntries(Object.entries(config).filter(([key]) => !key.startsWith('masked_')))
}

function oidcPayload(value: EmbedOidcFormValues) {
  return {
    client_record_id: value.clientRecordId || null,
  }
}

function translatedConfigFieldLabel(
  t: ReturnType<typeof useTranslation<'adminApps'>>['t'],
  field: AdminAppTypeConfigField,
) {
  const key = `drawer.configFields.${field.key}`
  const translated = t(key)

  return translated === key ? field.label : translated
}

function initialOidcValues(configuration?: AppOidcConfiguration | null): EmbedOidcFormValues {
  return {
    clientRecordId: configuration?.client?.id ?? '',
  }
}

function initialValues(app?: AdminApp | null, oidc?: AppOidcConfiguration | null): FormValues {
  if (!app) {
    return emptyFormValues
  }

  return {
    appType: app.app_type,
    avatar: app.avatar ?? null,
    config: normalizeInitialConfig(app.config ?? app.masked_config ?? {}),
    description: app.description,
    distributionMode: app.distribution_mode ?? 'manual',
    name: app.name,
    oidc: initialOidcValues(oidc),
    newTags: [],
    tagIds: (app.tags ?? []).map((tag) => tag.id),
    version: app.version ?? '',
  }
}

function normalizeTagName(value: string) {
  return value.trim().replace(/\s+/g, ' ')
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }

  return fallback
}

function validationFailureDetail(result: TencentAdpValidationResult) {
  return result.stages.find((stage) => stage.status === 'failed')?.message ?? result.message
}

function validationSuccessDetail(
  result: TencentAdpValidationResult,
  t: ReturnType<typeof useTranslation<'adminApps'>>['t'],
) {
  const appName = result.app_name?.trim()
  const appId = result.app_id?.trim()
  if (appName && appId) {
    return t('drawer.adpValidation.successDetail', {
      appId,
      name: appName,
    })
  }

  if (appId) {
    return t('drawer.adpValidation.successDetailWithoutName', { appId })
  }

  return t('drawer.adpValidation.successDetailWithoutAppId')
}

function nextCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.min(index + 1, createWizardSteps.length - 1)]
}

function previousCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.max(index - 1, 0)]
}

function fieldElementId(errorKey: string) {
  const fieldIds: Partial<Record<keyof FormValues, string>> = {
    appType: 'admin-app-type',
    name: 'admin-app-name',
    version: 'admin-app-version',
  }

  return fieldIds[errorKey as keyof FormValues] ?? `admin-app-config-${errorKey}`
}

function isHttpUrl(value: string) {
  return value.startsWith('http://') || value.startsWith('https://')
}

function isWebSocketUrl(value: string) {
  return value.startsWith('ws://') || value.startsWith('wss://')
}

function normalizeUrlTemplate(value: string) {
  return value.replace(urlTemplateVariablePattern, 'placeholder')
}

function matchesValidationPattern(value: string, pattern: string) {
  if (!pattern.trim()) {
    return true
  }

  try {
    return new RegExp(pattern).test(value)
  } catch {
    return true
  }
}

function canAdvanceCreateStep({
  isConfigLoading,
  isAdpCredentialsLoading,
  isRegisteredModelsLoading,
  step,
}: {
  isConfigLoading: boolean
  isAdpCredentialsLoading: boolean
  isRegisteredModelsLoading: boolean
  step: CreateWizardStep
}) {
  if (step === 'config') {
    return !isConfigLoading && !isRegisteredModelsLoading && !isAdpCredentialsLoading
  }

  return true
}

function registeredModelDisplayName(model: RegisteredModel) {
  return model.public_model_name || model.model_name
}

function registeredModelProviderLabel(model: RegisteredModel) {
  return model.provider_label ?? model.provider.toUpperCase()
}

function registeredModelOptionLabel(model: RegisteredModel) {
  return `${registeredModelProviderLabel(model)} / ${registeredModelDisplayName(model)}`
}

function adpCredentialOptionLabel(credential: TencentAdpCredentialOption) {
  return credential.name
}

export function AdminAppFormDrawer({
  appId,
  appTypes,
  formSession = 0,
  mode,
  onOpenChange,
  open,
}: AdminAppFormDrawerProps) {
  const { t } = useTranslation('adminApps')

  const detailQuery = useQuery({
    enabled: open && mode === 'edit' && Boolean(appId),
    queryFn: () => getAdminAppDetail(appId ?? ''),
    queryKey: ['admin-app-detail', appId],
  })

  const oidcQuery = useQuery({
    enabled:
      open && mode === 'edit' && detailQuery.data?.app_type === 'embed_url' && Boolean(appId),
    queryFn: () => getAdminAppOidcConfiguration(appId ?? ''),
    queryKey: ['admin-app-oidc', appId],
  })

  const drawerTitle =
    mode === 'create'
      ? t('drawer.createTitle')
      : t('drawer.editTitle', { name: detailQuery.data?.name ?? '' })

  const isLoading = mode === 'edit' && (detailQuery.isLoading || oidcQuery.isLoading)
  const isDetailError = mode === 'edit' && (detailQuery.isError || oidcQuery.isError)

  if (isLoading || isDetailError) {
    return (
      <Drawer onOpenChange={onOpenChange} open={open} size="lg" title={drawerTitle}>
        {isLoading ? <DrawerLoading /> : null}
        {isDetailError ? (
          <ErrorState
            action={
              <Button
                onClick={() => {
                  void detailQuery.refetch()
                  if (detailQuery.data?.app_type === 'embed_url') void oidcQuery.refetch()
                }}
                size="sm"
                variant="secondary"
              >
                {t('retry')}
              </Button>
            }
            description={formatApiError(
              detailQuery.error ?? oidcQuery.error,
              t('drawer.detailLoadFailed'),
            )}
            title={t('drawer.detailLoadFailedTitle')}
          />
        ) : null}
      </Drawer>
    )
  }

  return (
    <AdminAppFormContent
      app={mode === 'edit' ? detailQuery.data : null}
      appId={appId}
      appTypes={appTypes}
      formKey={`${mode}-${appId ?? 'new'}-${String(formSession)}-${
        detailQuery.data?.updated_at ?? 'empty'
      }`}
      initialOidc={oidcQuery.data}
      mode={mode}
      onOpenChange={onOpenChange}
      open={open}
      title={drawerTitle}
    />
  )
}

function AdminAppFormContent({
  app,
  appId,
  appTypes,
  formKey,
  initialOidc,
  mode,
  onOpenChange,
  open,
  title,
}: {
  app?: AdminApp | null
  appId?: string | null
  appTypes: AdminAppType[]
  formKey: string
  initialOidc?: AppOidcConfiguration | null
  mode: DrawerMode
  onOpenChange: (open: boolean) => void
  open: boolean
  title: string
}) {
  return (
    <KeyedAdminAppFormContent
      app={app}
      appId={appId}
      appTypes={appTypes}
      initialOidc={initialOidc}
      key={formKey}
      mode={mode}
      onOpenChange={onOpenChange}
      open={open}
      title={title}
    />
  )
}

function KeyedAdminAppFormContent({
  app,
  appId,
  appTypes,
  initialOidc,
  mode,
  onOpenChange,
  open,
  title,
}: {
  app?: AdminApp | null
  appId?: string | null
  appTypes: AdminAppType[]
  initialOidc?: AppOidcConfiguration | null
  mode: DrawerMode
  onOpenChange: (open: boolean) => void
  open: boolean
  title: string
}) {
  const { t } = useTranslation('adminApps')
  const queryClient = useQueryClient()
  const { dismissFeedback, showFeedback } = useFeedback()
  const [values, setValues] = useState<FormValues>(() => initialValues(app, initialOidc))
  const [errors, setErrors] = useState<FormErrors>({})
  const [createStep, setCreateStep] = useState<CreateWizardStep>('basic')
  const [editTab, setEditTab] = useState<EditDrawerTab>('basic')
  const [creationTargetStatus, setCreationTargetStatus] = useState<CreationTargetStatus>('draft')
  const [adpValidationIntent, setAdpValidationIntent] = useState<'manual' | 'submit' | null>(null)
  const [selectedSelfAgentTools, setSelectedSelfAgentTools] = useState<Set<string>>(new Set())
  const [debouncedName, setDebouncedName] = useState(values.name.trim())
  const [furthestCreateStepIndex, setFurthestCreateStepIndex] = useState(0)
  const [distributionDrafts, setDistributionDrafts] = useState<AdminAppMemberScopeDrafts>({})
  const distributionDraft = useMemo(
    () => mergeAdminAppMemberScopeDrafts(Object.values(distributionDrafts)),
    [distributionDrafts],
  )
  const handleDistributionDraftChange = useCallback(
    (companyId: string, draft: AdminAppMemberScopeDraft) => {
      setDistributionDrafts((current) => ({
        ...current,
        [companyId]: draft,
      }))
    },
    [],
  )

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedName(values.name.trim()), 400)
    return () => window.clearTimeout(timeoutId)
  }, [values.name])

  const nameAvailabilityQuery = useQuery({
    enabled: open && debouncedName.length > 0,
    queryFn: () =>
      getAdminAppNameAvailability(debouncedName, mode === 'edit' ? (appId ?? null) : null),
    queryKey: ['admin-app-name-availability', debouncedName.toLocaleLowerCase(), appId ?? null],
    retry: false,
  })

  const tagOptionsQuery = useQuery({
    queryFn: listAdminAppTagOptions,
    queryKey: ['admin-app-tag-options'],
  })

  const typeConfigQuery = useQuery({
    enabled: Boolean(values.appType),
    queryFn: () => getAdminAppTypeConfig(values.appType),
    queryKey: ['admin-app-type-config', values.appType],
  })

  const registeredModelsQuery = useQuery({
    enabled: values.appType === 'self_agent',
    queryFn: () =>
      listRegisteredModels({
        page: 1,
        pageSize: 200,
        status: 'active',
      }),
    queryKey: ['admin-app-registered-models', { pageSize: 200, status: 'active' }],
  })

  const agentToolCatalogQuery = useQuery({
    enabled: mode === 'create' && values.appType === 'self_agent',
    queryFn: getAgentToolCatalog,
    queryKey: ['admin-agent-tool-catalog'],
  })

  const adpCredentialsQuery = useQuery({
    enabled: values.appType === 'adp',
    queryFn: listTencentAdpCredentialOptions,
    queryKey: tencentAdpCredentialOptionsQueryKey,
  })

  const selectedType = useMemo(
    () => appTypes.find((item) => item.type === values.appType),
    [appTypes, values.appType],
  )
  const configFields = typeConfigQuery.data?.config_fields ?? selectedType?.config_fields ?? []
  const defaultConfig = typeConfigQuery.data?.default_config
  const effectiveConfig = useMemo(
    () => ({
      ...(defaultConfig ?? {}),
      ...values.config,
    }),
    [defaultConfig, values.config],
  )
  const displayValues = useMemo(
    () => ({
      ...values,
      config: effectiveConfig,
    }),
    [effectiveConfig, values],
  )

  const mutation = useMutation<AppFormMutationResult>({
    mutationFn: async () => {
      const payload = {
        appType: values.appType,
        avatar: undefined,
        config: submitConfig(effectiveConfig),
        description: values.description,
        distributionMode: values.distributionMode,
        name: values.name,
        newTags: values.newTags,
        tagIds: values.tagIds,
        version: values.version,
      }

      if (mode !== 'create') {
        const normalizedAppId = appId ?? ''
        const previousClientRecordId = initialOidc?.client?.id ?? null
        const updatedApp = await updateAdminApp(normalizedAppId, payload)
        const nextApp =
          values.avatar && values.avatar !== (app?.avatar ?? null)
            ? await updateAdminAppAvatar(updatedApp.id, values.avatar).then((icon) => ({
                ...updatedApp,
                app_icon: icon.app_icon,
                avatar: icon.avatar,
              }))
            : updatedApp
        const nextOidcPayload = oidcPayload(values.oidc)
        if (
          values.appType === 'embed_url' &&
          nextOidcPayload.client_record_id !== previousClientRecordId
        ) {
          await updateAdminAppOidcConfiguration(updatedApp.id, nextOidcPayload)
        }
        return { app: nextApp }
      }

      const createdApp = await createAdminApp(payload)
      let persistedApp = createdApp
      try {
        if (values.avatar) {
          const icon = await updateAdminAppAvatar(createdApp.id, values.avatar)
          persistedApp = {
            ...createdApp,
            app_icon: icon.app_icon,
            avatar: icon.avatar,
          }
        }
        if (values.appType === 'embed_url') {
          await updateAdminAppOidcConfiguration(createdApp.id, oidcPayload(values.oidc))
        }
        if (values.appType === 'self_agent' && selectedSelfAgentTools.size > 0) {
          const currentBindings = await getSelfAgentToolBindings(createdApp.id)
          await updateSelfAgentToolBindings(
            createdApp.id,
            buildAgentToolBindingsPayload(currentBindings, selectedSelfAgentTools),
          )
        }
        if (distributionDraft.accountIds.length > 0) {
          const result = await batchBindAdminAppMembers(createdApp.id, distributionDraft.accountIds)
          if (result.failed_count > 0) {
            throw new Error(t('drawer.wizard.distribution.assignmentFailed'))
          }
        }
        await Promise.all([
          ...distributionDraft.orgUnitIds.map((orgUnitId) =>
            bindAdminAppDepartment(createdApp.id, orgUnitId),
          ),
          ...distributionDraft.excludedAccountIds.map((accountId) =>
            createOrUpdateAdminAppMember({
              accountId,
              appId: createdApp.id,
              excluded: true,
            }),
          ),
        ])
      } catch (error) {
        await deleteAdminApp(createdApp.id).catch(() => undefined)
        throw error
      }
      let lifecycleWarning: string | undefined

      try {
        for (const action of creationTransitionActions[creationTargetStatus]) {
          await transitionAdminAppStatus({
            action,
            appId: createdApp.id,
            comment: t('drawer.wizard.lifecycle.transitionComment'),
          })
        }
      } catch {
        lifecycleWarning = t('drawer.wizard.lifecycle.partialSubmitFailed', {
          status: t(`status.${creationTargetStatus}`),
        })
      }

      return {
        app: persistedApp,
        lifecycleWarning,
      }
    },
    onError: (error) => {
      handleSubmitError(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-apps'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-app-tag-options'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-app-tags'] })
    },
    onSuccess: ({ lifecycleWarning }) => {
      dismissFeedback('drawer')
      if (mode === 'create') {
        showFeedback({
          message: lifecycleWarning ?? t('drawer.feedback.createSuccess'),
          scope: 'global',
          tone: lifecycleWarning ? 'warning' : 'success',
        })
      } else {
        showFeedback({
          message: t('drawer.feedback.updateSuccess'),
          scope: 'global',
          tone: 'success',
        })
      }
      onOpenChange(false)
    },
  })

  const adpValidationMutation = useMutation({
    mutationFn: (config: Record<string, AppConfigValue>) =>
      validateTencentAdpAppConfig({
        appId: mode === 'edit' && app?.app_type === 'adp' ? appId : null,
        config,
      }),
  })

  function updateValue<Key extends keyof FormValues>(key: Key, nextValue: FormValues[Key]) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      [key]: nextValue,
    }))
    setErrors((current) => ({
      ...current,
      [key]: undefined,
    }))
  }

  function updateConfig(key: string, nextValue: AppConfigValue) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      config: {
        ...current.config,
        [key]: nextValue,
      },
    }))
    setErrors((current) => ({
      ...current,
      [key]: undefined,
    }))
  }

  function validateConfigField(field: AdminAppTypeConfigField) {
    if (field.field_type === 'checkbox') {
      return undefined
    }

    const label = translatedConfigFieldLabel(t, field)
    const value = configValueToString(effectiveConfig[field.key]).trim()

    if (field.required && !(mode === 'edit' && field.is_sensitive) && !value) {
      return t('drawer.validation.configRequired', { label })
    }

    if (!value) {
      return undefined
    }

    if (field.key === 'api_host') {
      if (!isHttpUrl(value) || !value.replace(/\/+$/, '').endsWith('/v1')) {
        return t('drawer.validation.difyApiHost')
      }
    }

    if (field.key === 'chat_base_url') {
      if (!isWebSocketUrl(value)) {
        return t('drawer.validation.websocketUrl', { label })
      }

      try {
        new URL(value)
      } catch {
        return t('drawer.validation.configInvalid', { label })
      }

      if (value.includes('/chat/conn')) {
        return t('drawer.validation.chatBaseUrl')
      }
    } else if (field.field_type === 'url' || field.field_type === 'url_template') {
      const urlValue = field.field_type === 'url_template' ? normalizeUrlTemplate(value) : value
      if (!isHttpUrl(urlValue)) {
        return t('drawer.validation.urlProtocol', { label })
      }

      try {
        new URL(urlValue)
      } catch {
        return t('drawer.validation.configInvalid', { label })
      }
    }

    if (!matchesValidationPattern(value, field.validation_pattern)) {
      return t('drawer.validation.configInvalid', { label })
    }

    return undefined
  }

  function orderedErrorKeys(scope: CreateWizardStep | 'all') {
    const keys: string[] = []
    if (scope === 'all' || scope === 'basic') {
      keys.push('name', 'version')
    }
    if (scope === 'all' || scope === 'type') {
      keys.push('appType')
    }
    if (scope === 'all' || scope === 'config') {
      keys.push(...configFields.map((field) => field.key))
    }

    return keys
  }

  function focusErrorField(errorKey: string) {
    window.requestAnimationFrame(() => {
      document.getElementById(fieldElementId(errorKey))?.focus()
    })
  }

  function editTabForErrorKey(errorKey: string): EditDrawerTab {
    if (errorKey === 'name' || errorKey === 'version') {
      return 'basic'
    }

    return 'config'
  }

  function handleEditTabChange(tab: EditDrawerTab) {
    dismissFeedback('drawer')
    setEditTab(tab)
  }

  function announceValidationError(nextErrors: FormErrors, scope: CreateWizardStep | 'all') {
    const firstErrorKey =
      orderedErrorKeys(scope).find((key) => Boolean(nextErrors[key])) ?? Object.keys(nextErrors)[0]
    const firstError = firstErrorKey ? nextErrors[firstErrorKey] : undefined

    if (!firstErrorKey || !firstError) {
      return
    }

    const firstErrorTab = mode === 'edit' ? editTabForErrorKey(firstErrorKey) : null
    if (mode === 'edit' && scope === 'all') {
      setEditTab(firstErrorTab ?? 'basic')
    }
    focusErrorField(firstErrorKey)
    if (
      scope === 'config' ||
      (mode === 'edit' && firstErrorTab === 'config' && editTab === 'config')
    ) {
      return
    }

    showFeedback({
      detail: firstError,
      message: t('drawer.feedback.validationFailed'),
      scope: 'drawer',
      tone: 'error',
    })
  }

  function validateForm(scope: CreateWizardStep | 'all' = 'all') {
    const nextErrors: FormErrors = {}
    const shouldValidateBasic = scope === 'all' || scope === 'basic'
    const shouldValidateType = scope === 'all' || scope === 'type'
    const shouldValidateConfig = scope === 'all' || scope === 'config'

    if (shouldValidateBasic && !values.name.trim()) {
      nextErrors.name = t('drawer.validation.nameRequired')
    }

    if (shouldValidateBasic && !versionPattern.test(values.version.trim())) {
      nextErrors.version = t('drawer.validation.versionInvalid')
    }

    if (shouldValidateType && !values.appType.trim()) {
      nextErrors.appType = t('drawer.validation.typeRequired')
    }

    if (shouldValidateConfig) {
      for (const field of configFields) {
        const fieldError = validateConfigField(field)
        if (fieldError) {
          nextErrors[field.key] = fieldError
        }
      }
    }

    setErrors(nextErrors)

    const isValid = Object.keys(nextErrors).length === 0
    if (!isValid) {
      announceValidationError(nextErrors, scope)
    }

    return isValid
  }

  async function ensureNameAvailable() {
    const name = values.name.trim()
    if (!name) {
      return false
    }

    try {
      const result = await queryClient.fetchQuery({
        queryFn: () => getAdminAppNameAvailability(name, mode === 'edit' ? (appId ?? null) : null),
        queryKey: ['admin-app-name-availability', name.toLocaleLowerCase(), appId ?? null],
        staleTime: 0,
      })
      if (result.available) {
        setErrors((current) => ({ ...current, name: undefined }))
        return true
      }

      setErrors((current) => ({ ...current, name: t('drawer.validation.nameConflict') }))
      if (mode === 'create') setCreateStep('basic')
      else setEditTab('basic')
      focusErrorField('name')
      return false
    } catch {
      // 预检只用于提前反馈；网络失败时仍由最终创建/更新接口和 DB 唯一约束兜底。
      return true
    }
  }

  function mapBackendConfigError(message: string) {
    if (!message.includes('Configuration validation failed')) {
      return null
    }

    if (
      message.includes('API Host must start with http:// or https://') ||
      message.includes('API Host must use http(s)://<host>/v1 format and end with /v1')
    ) {
      return {
        key: 'api_host',
        message: t('drawer.validation.difyApiHost'),
      }
    }

    if (message.includes('API Host cannot be empty')) {
      return {
        key: 'api_host',
        message: t('drawer.validation.configRequired', {
          label: t('drawer.configFields.api_host'),
        }),
      }
    }

    if (message.includes('API Key cannot be empty')) {
      return {
        key: 'api_key',
        message: t('drawer.validation.configRequired', {
          label: t('drawer.configFields.api_key'),
        }),
      }
    }

    if (message.includes('Tencent ADP credential cannot be empty')) {
      return {
        key: 'credential_id',
        message: t('drawer.validation.configRequired', {
          label: t('drawer.configFields.credential_id'),
        }),
      }
    }

    if (message.includes('ADP credential does not exist')) {
      return {
        key: 'credential_id',
        message: t('drawer.adpCredentials.unavailable'),
      }
    }

    if (message.includes('ADP chat base URL must start with ws:// or wss://')) {
      return {
        key: 'chat_base_url',
        message: t('drawer.validation.websocketUrl', {
          label: t('drawer.configFields.chat_base_url'),
        }),
      }
    }

    if (message.includes('ADP chat base URL is invalid')) {
      return {
        key: 'chat_base_url',
        message: t('drawer.validation.configInvalid', {
          label: t('drawer.configFields.chat_base_url'),
        }),
      }
    }

    if (message.includes('ADP chat base URL should not include /chat/conn')) {
      return {
        key: 'chat_base_url',
        message: t('drawer.validation.chatBaseUrl'),
      }
    }

    if (message.includes('App URL template must start with http:// or https://')) {
      return {
        key: 'url_template',
        message: t('drawer.validation.urlProtocol', {
          label: t('drawer.configFields.url_template'),
        }),
      }
    }

    return null
  }

  function handleSubmitError(error: unknown) {
    if (error instanceof ApiError && error.errorCode === 'app.name_conflict') {
      if (mode === 'create') setCreateStep('basic')
      else setEditTab('basic')
      setErrors((current) => ({ ...current, name: t('drawer.validation.nameConflict') }))
      showFeedback({
        detail: t('drawer.validation.nameConflict'),
        message: t('drawer.feedback.validationFailed'),
        scope: 'drawer',
        tone: 'error',
      })
      focusErrorField('name')
      return
    }

    const message = formatApiError(error, t('drawer.submitFailed'))
    const configError = mapBackendConfigError(message)

    if (configError) {
      if (mode === 'create') {
        setCreateStep('config')
      } else {
        setEditTab('config')
      }
      setErrors((current) => ({
        ...current,
        [configError.key]: configError.message,
      }))
      showFeedback({
        detail: configError.message,
        message: t('drawer.feedback.configValidationFailed'),
        scope: 'drawer',
        tone: 'error',
      })
      focusErrorField(configError.key)
      return
    }

    showFeedback({
      detail: message,
      message: t('drawer.feedback.submitFailed'),
      scope: 'drawer',
      tone: 'error',
    })
  }

  async function runAdpAppValidation(intent: 'manual' | 'submit') {
    if (values.appType !== 'adp') {
      return true
    }
    if (!validateForm('config')) {
      return false
    }

    setAdpValidationIntent(intent)
    try {
      const result = await adpValidationMutation.mutateAsync(submitConfig(effectiveConfig))
      if (!result.ok) {
        showFeedback({
          detail: validationFailureDetail(result),
          message: t('drawer.feedback.adpValidationFailed'),
          scope: 'drawer',
          tone: 'error',
        })
        return false
      }
      if (intent === 'manual') {
        showFeedback({
          detail: validationSuccessDetail(result, t),
          message: t('drawer.feedback.adpValidationPassed'),
          scope: 'drawer',
          tone: 'success',
        })
      }
      return true
    } catch (error) {
      showFeedback({
        detail: formatApiError(error, t('drawer.feedback.adpValidationFailed')),
        message: t('drawer.feedback.adpValidationFailed'),
        scope: 'drawer',
        tone: 'error',
      })
      return false
    } finally {
      setAdpValidationIntent(null)
    }
  }

  function handleManualAdpAppValidation() {
    void runAdpAppValidation('manual')
  }

  async function handleSubmit() {
    if (!validateForm()) {
      return
    }

    if (!(await ensureNameAvailable())) {
      return
    }

    if (!(await runAdpAppValidation('submit'))) {
      if (mode === 'create') {
        setCreateStep('config')
      } else {
        setEditTab('config')
      }
      return
    }

    mutation.mutate()
  }

  async function handleCreatePrimaryAction() {
    if (createStep !== 'lifecycle') {
      if (!validateForm(createStep)) {
        return
      }

      if (createStep === 'basic' && !(await ensureNameAvailable())) {
        return
      }

      const nextStep = nextCreateStep(createStep)
      setCreateStep(nextStep)
      setFurthestCreateStepIndex((current) =>
        Math.max(current, createWizardSteps.indexOf(nextStep)),
      )
      return
    }

    void handleSubmit()
  }

  function handleCreateSecondaryAction() {
    if (createStep === 'basic') {
      onOpenChange(false)
      return
    }

    setCreateStep(previousCreateStep(createStep))
  }

  const isConfigLoading =
    Boolean(values.appType) && typeConfigQuery.isLoading && !selectedType?.config_fields?.length
  const registeredModelSelectState: RegisteredModelSelectState = {
    isError: registeredModelsQuery.isError,
    isLoading: registeredModelsQuery.isLoading,
    models: registeredModelsQuery.data?.items ?? [],
  }
  const adpCredentialSelectState: TencentAdpCredentialSelectState = {
    credentials: adpCredentialsQuery.data ?? [],
    isError: adpCredentialsQuery.isError,
    isLoading: adpCredentialsQuery.isLoading,
    refresh: () => {
      void adpCredentialsQuery.refetch()
    },
  }
  const isRegisteredModelsLoading =
    values.appType === 'self_agent' && registeredModelSelectState.isLoading
  const isAdpCredentialsLoading = values.appType === 'adp' && adpCredentialSelectState.isLoading
  const createStepIndex = createWizardSteps.indexOf(createStep)
  const canAdvanceCurrentCreateStep = canAdvanceCreateStep({
    isConfigLoading,
    isAdpCredentialsLoading,
    isRegisteredModelsLoading,
    step: createStep,
  })
  const isAdpValidationPending = adpValidationIntent !== null
  const nameAvailabilityHint = !values.name.trim()
    ? undefined
    : debouncedName !== values.name.trim() || nameAvailabilityQuery.isFetching
      ? t('drawer.nameAvailability.checking')
      : nameAvailabilityQuery.data?.available
        ? t('drawer.nameAvailability.available')
        : nameAvailabilityQuery.isError
          ? t('drawer.nameAvailability.failed')
          : undefined
  const nameAvailabilityConflict =
    debouncedName === values.name.trim() && nameAvailabilityQuery.data?.available === false
  const displayErrors: FormErrors = nameAvailabilityConflict
    ? { ...errors, name: t('drawer.validation.nameConflict') }
    : errors
  const drawerHeaderAddon =
    mode === 'create' ? (
      <DrawerStepProgress
        ariaLabel={t('drawer.wizard.progressLabel', {
          current: createStepIndex + 1,
          step: t(`drawer.wizard.steps.${createStep}.title`),
          total: createWizardSteps.length,
        })}
        canSelectStep={(_step, index) => index <= furthestCreateStepIndex}
        className="w-full"
        currentStep={createStep}
        getStepLabel={(step) => t(`drawer.wizard.steps.${step}.title`)}
        onStepChange={(step) => {
          if (createWizardSteps.indexOf(step) <= furthestCreateStepIndex) {
            dismissFeedback('drawer')
            setCreateStep(step)
          }
        }}
        steps={createWizardSteps}
      />
    ) : (
      <EditDrawerTabs activeTab={editTab} onTabChange={handleEditTabChange} />
    )

  const drawerFooter =
    mode === 'create' ? (
      <DrawerWizardFooter
        backLabel={t('drawer.wizard.previous')}
        canGoBack={createStep !== 'basic'}
        isLoading={mutation.isPending || adpValidationIntent === 'submit'}
        nextLabel={createStep === 'lifecycle' ? t('drawer.wizard.create') : t('drawer.wizard.next')}
        nextDisabled={!canAdvanceCurrentCreateStep || isAdpValidationPending}
        onBack={handleCreateSecondaryAction}
        onNext={() => void handleCreatePrimaryAction()}
      />
    ) : (
      <>
        <Button
          className="h-[38px] min-w-[60px] rounded-md border-[var(--color-control-border)] px-4 font-normal"
          disabled={mutation.isPending || isAdpValidationPending}
          onClick={() => onOpenChange(false)}
          size="sm"
          variant="secondary"
        >
          {t('drawer.cancel')}
        </Button>
        <Button
          className="h-[38px] min-w-[60px] rounded-md px-4 font-normal"
          disabled={isConfigLoading || isAdpValidationPending}
          isLoading={mutation.isPending || adpValidationIntent === 'submit'}
          onClick={() => {
            void handleSubmit()
          }}
          size="sm"
        >
          {t('drawer.save')}
        </Button>
      </>
    )

  return (
    <Drawer
      bodyClassName={
        mode === 'create' && createStep === 'distribution'
          ? 'overflow-hidden px-6 pt-5 pb-0'
          : undefined
      }
      footer={drawerFooter}
      headerAddon={drawerHeaderAddon}
      onOpenChange={onOpenChange}
      open={open}
      size={
        mode === 'create' && (createStep === 'distribution' || createStep === 'lifecycle')
          ? 'xl'
          : 'lg'
      }
      title={title}
    >
      <div
        className={cn(
          mode === 'create' && createStep === 'distribution' ? 'h-full min-h-0' : 'pb-6',
        )}
      >
        {mode === 'create' ? (
          <CreateWizardStepContent
            adpCredentials={adpCredentialSelectState}
            agentToolCatalogError={agentToolCatalogQuery.error}
            agentToolCatalogLoading={agentToolCatalogQuery.isLoading}
            agentToolCatalog={agentToolCatalogQuery.data?.tools ?? []}
            appTypes={appTypes}
            configFields={configFields}
            creationTargetStatus={creationTargetStatus}
            errors={displayErrors}
            isAdpValidationPending={adpValidationIntent === 'manual'}
            isConfigLoading={isConfigLoading}
            nameAvailabilityHint={nameAvailabilityHint}
            onConfigChange={updateConfig}
            distributionDrafts={distributionDrafts}
            onDistributionDraftChange={handleDistributionDraftChange}
            onOidcChange={(oidc) => updateValue('oidc', oidc)}
            onValidateAdpApp={handleManualAdpAppValidation}
            onTargetStatusChange={setCreationTargetStatus}
            onTypeChange={(value) => {
              if (value !== 'self_agent') setSelectedSelfAgentTools(new Set())
              updateValue('appType', value)
              updateValue('config', {})
            }}
            onRetryAgentToolCatalog={() => void agentToolCatalogQuery.refetch()}
            onSelectedAgentToolsChange={setSelectedSelfAgentTools}
            onValueChange={updateValue}
            registeredModels={registeredModelSelectState}
            step={createStep}
            selectedAgentTools={selectedSelfAgentTools}
            tagOptions={tagOptionsQuery.data ?? []}
            values={displayValues}
          />
        ) : (
          <EditDrawerTabContent
            activeTab={editTab}
            adpCredentials={adpCredentialSelectState}
            app={app}
            appTypes={appTypes}
            configFields={configFields}
            errors={displayErrors}
            isAdpValidationPending={adpValidationIntent === 'manual'}
            isConfigLoading={isConfigLoading}
            nameAvailabilityHint={nameAvailabilityHint}
            onConfigChange={updateConfig}
            onOidcChange={(oidc) => updateValue('oidc', oidc)}
            onValidateAdpApp={handleManualAdpAppValidation}
            onTypeChange={(value) => {
              updateValue('appType', value)
              updateValue('config', {})
            }}
            onValueChange={updateValue}
            registeredModels={registeredModelSelectState}
            tagOptions={tagOptionsQuery.data ?? []}
            values={displayValues}
          />
        )}
      </div>
    </Drawer>
  )
}

function EditDrawerTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: EditDrawerTab
  onTabChange: (tab: EditDrawerTab) => void
}) {
  const { t } = useTranslation('adminApps')

  return (
    <DrawerTabs
      activeTab={activeTab}
      ariaLabel={t('drawer.editTabs.label')}
      onTabChange={onTabChange}
      tabs={editDrawerTabs.map((tab) => ({
        id: tab,
        label: t(`drawer.editTabs.${tab}`),
        panelId: `admin-app-edit-panel-${tab}`,
        tabId: `admin-app-edit-tab-${tab}`,
      }))}
    />
  )
}

function EditDrawerTabContent({
  activeTab,
  adpCredentials,
  app,
  appTypes,
  configFields,
  errors,
  isAdpValidationPending,
  isConfigLoading,
  nameAvailabilityHint,
  onConfigChange,
  onOidcChange,
  onValidateAdpApp,
  onTypeChange,
  onValueChange,
  registeredModels,
  tagOptions,
  values,
}: {
  activeTab: EditDrawerTab
  adpCredentials: TencentAdpCredentialSelectState
  app?: AdminApp | null
  appTypes: AdminAppType[]
  configFields: AdminAppTypeConfigField[]
  errors: FormErrors
  isAdpValidationPending: boolean
  isConfigLoading: boolean
  nameAvailabilityHint?: string
  onConfigChange: (key: string, value: AppConfigValue) => void
  onOidcChange: (value: EmbedOidcFormValues) => void
  onValidateAdpApp: () => void
  onTypeChange: (value: string) => void
  onValueChange: <Key extends keyof FormValues>(key: Key, nextValue: FormValues[Key]) => void
  registeredModels: RegisteredModelSelectState
  tagOptions: AdminAppTag[]
  values: FormValues
}) {
  let content: ReactNode = null

  switch (activeTab) {
    case 'basic':
      content = (
        <EditBasicInfoTab
          errors={errors}
          nameAvailabilityHint={nameAvailabilityHint}
          onValueChange={onValueChange}
          tagOptions={tagOptions}
          values={values}
        />
      )
      break
    case 'config':
      content = (
        <EditAppConfigTab
          adpCredentials={adpCredentials}
          app={app}
          appTypes={appTypes}
          configFields={configFields}
          errors={errors}
          isAdpValidationPending={isAdpValidationPending}
          isConfigLoading={isConfigLoading}
          onConfigChange={onConfigChange}
          onOidcChange={onOidcChange}
          onValidateAdpApp={onValidateAdpApp}
          onTypeChange={onTypeChange}
          registeredModels={registeredModels}
          values={values}
        />
      )
      break
    case 'lifecycle':
      content = <EditLifecycleTab app={app} />
      break
  }

  return (
    <div
      aria-labelledby={`admin-app-edit-tab-${activeTab}`}
      id={`admin-app-edit-panel-${activeTab}`}
      role="tabpanel"
      tabIndex={0}
    >
      {content}
    </div>
  )
}

function EditBasicInfoTab({
  errors,
  nameAvailabilityHint,
  onValueChange,
  tagOptions,
  values,
}: {
  errors: FormErrors
  nameAvailabilityHint?: string
  onValueChange: <Key extends keyof FormValues>(key: Key, nextValue: FormValues[Key]) => void
  tagOptions: AdminAppTag[]
  values: FormValues
}) {
  return (
    <div className="grid gap-6">
      <CreateBasicInfoStep
        errors={errors}
        nameAvailabilityHint={nameAvailabilityHint}
        onValueChange={onValueChange}
        tagOptions={tagOptions}
        values={values}
      />
      <DistributionModePicker
        mode={values.distributionMode}
        onModeChange={(distributionMode) => onValueChange('distributionMode', distributionMode)}
      />
    </div>
  )
}

function EditAppConfigTab({
  adpCredentials,
  app,
  appTypes,
  configFields,
  errors,
  isAdpValidationPending,
  isConfigLoading,
  onConfigChange,
  onOidcChange,
  onValidateAdpApp,
  onTypeChange,
  registeredModels,
  values,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  app?: AdminApp | null
  appTypes: AdminAppType[]
  configFields: AdminAppTypeConfigField[]
  errors: FormErrors
  isAdpValidationPending: boolean
  isConfigLoading: boolean
  onConfigChange: (key: string, value: AppConfigValue) => void
  onOidcChange: (value: EmbedOidcFormValues) => void
  onValidateAdpApp: () => void
  onTypeChange: (value: string) => void
  registeredModels: RegisteredModelSelectState
  values: FormValues
}) {
  const { t } = useTranslation('adminApps')
  const appTypeOptions = [
    { label: t('drawer.placeholders.appType'), value: '' },
    ...appTypes.map((item) => ({
      label: item.label,
      value: item.type,
    })),
  ]

  const validationAction =
    values.appType === 'adp' ? (
      <AdpAppValidationButton
        disabled={isConfigLoading}
        isLoading={isAdpValidationPending}
        onClick={onValidateAdpApp}
      />
    ) : null

  return (
    <DrawerFormLayout className="mt-0">
      <DrawerStepTitle action={validationAction} compact title={t('drawer.fields.appConfig')} />
      <DrawerFormRow columns={1}>
        <FormField
          error={errors.appType}
          errorDisplay="sr-only"
          htmlFor="admin-app-type"
          label={t('drawer.fields.appType')}
          required
        >
          <Select
            aria-label={t('drawer.fields.appType')}
            id="admin-app-type"
            invalid={Boolean(errors.appType)}
            onValueChange={onTypeChange}
            options={appTypeOptions}
            value={values.appType}
          />
        </FormField>
      </DrawerFormRow>
      {isConfigLoading ? <AppConfigSectionSkeleton framed={false} /> : null}
      {!isConfigLoading && values.appType && configFields.length > 0 ? (
        <AppConfigSection
          errors={errors}
          fields={configFields}
          framed={false}
          layout={false}
          adpCredentials={adpCredentials}
          onConfigChange={onConfigChange}
          registeredModels={registeredModels}
          values={values.config}
        />
      ) : null}
      {!isConfigLoading && (!values.appType || configFields.length === 0) ? (
        <div className="rounded-md bg-[rgba(26,28,25,0.025)] px-3 py-2">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('drawer.editTabs.configEmpty')}
          </p>
        </div>
      ) : null}
      {!isConfigLoading && values.appType === 'embed_url' ? (
        <EmbedOidcBindingSection onChange={onOidcChange} value={values.oidc} />
      ) : null}
      {app?.app_type === 'self_agent' && values.appType === 'self_agent' ? (
        <div className="mt-6 border-t border-[var(--color-divider)] pt-6">
          <AgentToolBindingCheckboxes owner={{ agentId: app.id, agentType: 'self_agent' }} />
        </div>
      ) : null}
    </DrawerFormLayout>
  )
}

function EditLifecycleTab({ app }: { app?: AdminApp | null }) {
  const { t } = useTranslation('adminApps')
  const status = app?.status ?? ''
  const transitionsQuery = useQuery({
    enabled: Boolean(app?.id),
    queryFn: () => listAdminAppTransitions(app?.id ?? ''),
    queryKey: ['admin-app-review-transitions', app?.id],
  })

  return (
    <div className="grid gap-6">
      <section className="grid gap-3 rounded-lg border border-[var(--color-panel-border)] bg-[#fbfbfb] p-4">
        {app ? (
          <AdminAppNameCell
            app={app}
            subtitle={app.description || t('review.drawer.noDescription')}
          />
        ) : (
          <div className="h-11 animate-pulse rounded-md bg-[var(--color-skeleton)]" />
        )}
        <AdminAppLifecycleProgress status={status} t={t} />
      </section>
      <dl className="grid gap-3 rounded-lg border border-[var(--color-panel-border)] bg-white p-4 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-xs text-[var(--color-text-muted)]">
            {t('drawer.editTabs.createdAt')}
          </dt>
          <dd className="mt-1 truncate text-sm font-medium text-[var(--color-text)]">
            {app?.created_at ? formatDateTime(app.created_at) : '-'}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-[var(--color-text-muted)]">
            {t('drawer.editTabs.updatedAt')}
          </dt>
          <dd className="mt-1 truncate text-sm font-medium text-[var(--color-text)]">
            {app?.updated_at ? formatDateTime(app.updated_at) : '-'}
          </dd>
        </div>
      </dl>
      <section className="grid gap-3">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">
          {t('review.drawer.history')}
        </h3>
        {transitionsQuery.isError ? (
          <InlineNotice tone="danger">{t('review.drawer.historyLoadFailed')}</InlineNotice>
        ) : transitionsQuery.isLoading ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }, (_, index) => (
              <div
                className="h-[74px] animate-pulse rounded-lg bg-[var(--color-skeleton)]"
                key={index}
              />
            ))}
          </div>
        ) : (
          <AdminAppTransitionHistory items={transitionsQuery.data ?? []} t={t} />
        )}
      </section>
    </div>
  )
}

function CreateWizardStepContent({
  adpCredentials,
  agentToolCatalog,
  agentToolCatalogError,
  agentToolCatalogLoading,
  appTypes,
  configFields,
  creationTargetStatus,
  distributionDrafts,
  errors,
  isAdpValidationPending,
  isConfigLoading,
  nameAvailabilityHint,
  onConfigChange,
  onDistributionDraftChange,
  onOidcChange,
  onRetryAgentToolCatalog,
  onSelectedAgentToolsChange,
  onValidateAdpApp,
  onTargetStatusChange,
  onTypeChange,
  onValueChange,
  registeredModels,
  selectedAgentTools,
  step,
  tagOptions,
  values,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  agentToolCatalog: AgentToolOption[]
  agentToolCatalogError: unknown
  agentToolCatalogLoading: boolean
  appTypes: AdminAppType[]
  configFields: AdminAppTypeConfigField[]
  creationTargetStatus: CreationTargetStatus
  distributionDrafts: AdminAppMemberScopeDrafts
  errors: FormErrors
  isAdpValidationPending: boolean
  isConfigLoading: boolean
  nameAvailabilityHint?: string
  onConfigChange: (key: string, value: AppConfigValue) => void
  onDistributionDraftChange: (companyId: string, draft: AdminAppMemberScopeDraft) => void
  onOidcChange: (value: EmbedOidcFormValues) => void
  onRetryAgentToolCatalog: () => void
  onSelectedAgentToolsChange: (selected: Set<string>) => void
  onValidateAdpApp: () => void
  onTargetStatusChange: (status: CreationTargetStatus) => void
  onTypeChange: (value: string) => void
  onValueChange: <Key extends keyof FormValues>(key: Key, nextValue: FormValues[Key]) => void
  registeredModels: RegisteredModelSelectState
  selectedAgentTools: ReadonlySet<string>
  step: CreateWizardStep
  tagOptions: AdminAppTag[]
  values: FormValues
}) {
  let content: ReactNode = null

  switch (step) {
    case 'basic':
      content = (
        <CreateBasicInfoStep
          errors={errors}
          nameAvailabilityHint={nameAvailabilityHint}
          onValueChange={onValueChange}
          tagOptions={tagOptions}
          values={values}
        />
      )
      break
    case 'type':
      content = (
        <CreateAppTypeStep
          appTypes={appTypes}
          error={errors.appType}
          onTypeChange={onTypeChange}
          value={values.appType}
        />
      )
      break
    case 'config':
      content = (
        <CreateAppConfigStep
          adpCredentials={adpCredentials}
          agentToolCatalog={agentToolCatalog}
          agentToolCatalogError={agentToolCatalogError}
          agentToolCatalogLoading={agentToolCatalogLoading}
          configFields={configFields}
          errors={errors}
          isConfigLoading={isConfigLoading}
          onConfigChange={onConfigChange}
          onOidcChange={onOidcChange}
          onRetryAgentToolCatalog={onRetryAgentToolCatalog}
          onSelectedAgentToolsChange={onSelectedAgentToolsChange}
          registeredModels={registeredModels}
          selectedAgentTools={selectedAgentTools}
          values={values}
        />
      )
      break
    case 'distribution':
      content = (
        <CreateDistributionStep
          drafts={distributionDrafts}
          onDraftChange={onDistributionDraftChange}
        />
      )
      break
    case 'lifecycle':
      content = (
        <CreateLifecycleStep
          distributionMode={values.distributionMode}
          onDistributionModeChange={(distributionMode) =>
            onValueChange('distributionMode', distributionMode)
          }
          onTargetStatusChange={onTargetStatusChange}
          targetStatus={creationTargetStatus}
        />
      )
      break
  }

  return (
    <div className={cn(step === 'distribution' && 'flex h-full min-h-0 flex-col')}>
      {step === 'distribution' ? null : (
        <CreateStepTitle
          action={
            step === 'config' && values.appType === 'adp' ? (
              <AdpAppValidationButton
                disabled={isConfigLoading}
                isLoading={isAdpValidationPending}
                onClick={onValidateAdpApp}
              />
            ) : null
          }
          step={step}
        />
      )}
      <div className={cn(step === 'distribution' && 'min-h-0 flex-1')}>{content}</div>
    </div>
  )
}

function CreateStepTitle({ action, step }: { action?: ReactNode; step: CreateWizardStep }) {
  const { t } = useTranslation('adminApps')

  return <DrawerStepTitle action={action} title={t(`drawer.wizard.steps.${step}.title`)} />
}

function CreateBasicInfoStep({
  errors,
  nameAvailabilityHint,
  onValueChange,
  tagOptions,
  values,
}: {
  errors: FormErrors
  nameAvailabilityHint?: string
  onValueChange: <Key extends keyof FormValues>(key: Key, nextValue: FormValues[Key]) => void
  tagOptions: AdminAppTag[]
  values: FormValues
}) {
  const { t } = useTranslation('adminApps')
  const displayName = values.name.trim().length > 0 ? values.name : t('drawer.avatarFallback')

  return (
    <DrawerFormLayout className="mt-0">
      <DrawerFormRow columns={1}>
        <FormField
          error={errors.name}
          hint={nameAvailabilityHint}
          htmlFor="admin-app-name"
          label={t('drawer.fields.name')}
          required
        >
          <Input
            id="admin-app-name"
            invalid={Boolean(errors.name)}
            onChange={(event) => onValueChange('name', event.target.value)}
            placeholder={t('drawer.placeholders.name')}
            value={values.name}
          />
        </FormField>
      </DrawerFormRow>
      <DrawerFormRow columns={1}>
        <FormField htmlFor="admin-app-avatar" label={t('drawer.fields.avatar')}>
          <AdminAppIconPicker
            inputId="admin-app-avatar"
            name={displayName}
            onChange={(avatar) => onValueChange('avatar', avatar)}
            value={values.avatar}
          />
        </FormField>
      </DrawerFormRow>
      <DrawerFormRow columns={1}>
        <FormField htmlFor="admin-app-description" label={t('drawer.fields.description')}>
          <Textarea
            id="admin-app-description"
            onChange={(event) => onValueChange('description', event.target.value)}
            placeholder={t('drawer.placeholders.description')}
            value={values.description}
          />
        </FormField>
      </DrawerFormRow>
      <DrawerFormRow columns={1}>
        <FormField
          error={errors.version}
          errorDisplay="sr-only"
          htmlFor="admin-app-version"
          label={t('drawer.fields.version')}
          labelExtra={<FieldHelp label={t('drawer.wizard.basic.versionHint')} />}
        >
          <Input
            id="admin-app-version"
            invalid={Boolean(errors.version)}
            onChange={(event) => onValueChange('version', event.target.value)}
            placeholder={t('drawer.placeholders.version')}
            value={values.version}
          />
        </FormField>
      </DrawerFormRow>
      <DrawerFormRow columns={1}>
        <FormField htmlFor="admin-app-tags" label={t('drawer.fields.tags')}>
          <EditableTagSelector
            emptyText={t('drawer.placeholders.tagsEmpty')}
            id="admin-app-tags"
            onChange={(nextValue) => {
              onValueChange('tagIds', nextValue.tagIds)
              onValueChange('newTags', nextValue.newTags)
            }}
            options={tagOptions}
            placeholder={t('drawer.placeholders.tags')}
            value={{ newTags: values.newTags, tagIds: values.tagIds }}
          />
        </FormField>
      </DrawerFormRow>
    </DrawerFormLayout>
  )
}

function appTypeDescription(t: ReturnType<typeof useTranslation<'adminApps'>>['t'], type: string) {
  const key = `drawer.wizard.appTypeDescriptions.${type}`
  const translated = t(key)

  return translated === key ? t('drawer.wizard.appTypeDescriptions.default') : translated
}

function CreateAppTypeStep({
  appTypes,
  error,
  onTypeChange,
  value,
}: {
  appTypes: AdminAppType[]
  error?: string
  onTypeChange: (value: string) => void
  value: string
}) {
  const { t } = useTranslation('adminApps')

  return (
    <div>
      <div
        aria-invalid={Boolean(error) || undefined}
        aria-label={t('drawer.fields.appType')}
        className="grid gap-3"
        id="admin-app-type"
        role="radiogroup"
        tabIndex={-1}
      >
        {appTypes.map((item) => {
          const selected = value === item.type

          return (
            <SelectableCard
              as="button"
              aria-checked={selected}
              className="min-h-[76px] cursor-pointer px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
              key={item.type}
              onClick={() => onTypeChange(item.type)}
              role="radio"
              selected={selected}
            >
              <span className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-[var(--color-text)]">
                    {item.label}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                    {appTypeDescription(t, item.type)}
                  </span>
                </span>
                <SelectableCardIndicator selected={selected} />
              </span>
            </SelectableCard>
          )
        })}
      </div>
      {error ? <p className="sr-only">{error}</p> : null}
    </div>
  )
}

function CreateAppConfigStep({
  adpCredentials,
  agentToolCatalog,
  agentToolCatalogError,
  agentToolCatalogLoading,
  configFields,
  errors,
  isConfigLoading,
  onConfigChange,
  onOidcChange,
  onRetryAgentToolCatalog,
  onSelectedAgentToolsChange,
  registeredModels,
  selectedAgentTools,
  values,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  agentToolCatalog: AgentToolOption[]
  agentToolCatalogError: unknown
  agentToolCatalogLoading: boolean
  configFields: AdminAppTypeConfigField[]
  errors: FormErrors
  isConfigLoading: boolean
  onConfigChange: (key: string, value: AppConfigValue) => void
  onOidcChange: (value: EmbedOidcFormValues) => void
  onRetryAgentToolCatalog: () => void
  onSelectedAgentToolsChange: (selected: Set<string>) => void
  registeredModels: RegisteredModelSelectState
  selectedAgentTools: ReadonlySet<string>
  values: FormValues
}) {
  const { t } = useTranslation('adminApps')
  const selfAgentToolDetailsField =
    values.appType === 'self_agent'
      ? configFields.find((field) => field.key === 'show_tool_invocation_details')
      : undefined
  const visibleConfigFields = selfAgentToolDetailsField
    ? configFields.filter((field) => field.key !== selfAgentToolDetailsField.key)
    : configFields

  if (isConfigLoading) {
    return <AppConfigSectionSkeleton framed={false} />
  }

  return (
    <div>
      {values.appType && visibleConfigFields.length > 0 ? (
        <AppConfigSection
          errors={errors}
          framed={false}
          fields={visibleConfigFields}
          adpCredentials={adpCredentials}
          onConfigChange={onConfigChange}
          registeredModels={registeredModels}
          values={values.config}
        />
      ) : (
        <div className="rounded-md bg-[rgba(26,28,25,0.025)] px-3 py-2">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('drawer.wizard.config.empty')}
          </p>
        </div>
      )}
      {values.appType === 'embed_url' ? (
        <EmbedOidcBindingSection onChange={onOidcChange} value={values.oidc} />
      ) : null}
      {values.appType === 'self_agent' ? (
        <div className="mt-5">
          <AgentToolDraftSelector
            error={agentToolCatalogError}
            isLoading={agentToolCatalogLoading}
            onRetry={onRetryAgentToolCatalog}
            onSelectedChange={onSelectedAgentToolsChange}
            selected={selectedAgentTools}
            tools={agentToolCatalog}
          />
          {selfAgentToolDetailsField ? (
            <div className="mt-4 flex min-h-11 items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--color-text)]">
                  {translatedConfigFieldLabel(t, selfAgentToolDetailsField)}
                </div>
                <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-muted)]">
                  {t('drawer.configFieldHints.show_tool_invocation_details')}
                </p>
              </div>
              <Switch
                aria-label={translatedConfigFieldLabel(t, selfAgentToolDetailsField)}
                checked={configValueToBoolean(values.config[selfAgentToolDetailsField.key])}
                className="mt-0.5 shrink-0"
                onCheckedChange={(checked) =>
                  onConfigChange(selfAgentToolDetailsField.key, checked)
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function DistributionModePicker({
  compact = false,
  mode,
  onModeChange,
}: {
  compact?: boolean
  mode: FormValues['distributionMode']
  onModeChange: (mode: FormValues['distributionMode']) => void
}) {
  const { t } = useTranslation('adminApps')

  if (compact) {
    return (
      <section className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-4 py-3">
        <div className="min-w-[220px] flex-1">
          <h4 className="text-sm font-semibold text-[var(--color-text)]">
            {t('drawer.wizard.distribution.modeTitle')}
          </h4>
          <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-muted)]">
            {t('drawer.wizard.distribution.modeDescription')}
          </p>
        </div>
        <div
          className="flex shrink-0 rounded-md border border-[var(--color-control-border)] bg-white p-0.5"
          role="radiogroup"
        >
          {(['manual', 'automatic'] as const).map((item) => {
            const selected = mode === item
            return (
              <button
                aria-checked={selected}
                aria-label={`${t(`drawer.wizard.distribution.modes.${item}.title`)}：${t(
                  `drawer.wizard.distribution.modes.${item}.description`,
                )}`}
                className={cn(
                  'h-8 cursor-pointer rounded px-3 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]',
                  selected
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-hover-muted)] hover:text-[var(--color-text)]',
                )}
                key={item}
                onClick={() => onModeChange(item)}
                role="radio"
                type="button"
              >
                {t(`drawer.wizard.distribution.modes.${item}.title`)}
              </button>
            )
          })}
        </div>
      </section>
    )
  }

  return (
    <section className="grid gap-3">
      <div>
        <h4 className="text-sm font-semibold text-[var(--color-text)]">
          {t('drawer.wizard.distribution.modeTitle')}
        </h4>
        <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
          {t('drawer.wizard.distribution.modeDescription')}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2" role="radiogroup">
        {(['manual', 'automatic'] as const).map((item) => {
          const selected = mode === item
          return (
            <SelectableCard
              as="button"
              aria-checked={selected}
              className="cursor-pointer px-4 py-3 text-left"
              key={item}
              onClick={() => onModeChange(item)}
              role="radio"
              selected={selected}
            >
              <span className="flex items-start justify-between gap-3">
                <span>
                  <span className="block text-sm font-bold text-[var(--color-text)]">
                    {t(`drawer.wizard.distribution.modes.${item}.title`)}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                    {t(`drawer.wizard.distribution.modes.${item}.description`)}
                  </span>
                </span>
                <SelectableCardIndicator selected={selected} />
              </span>
            </SelectableCard>
          )
        })}
      </div>
    </section>
  )
}

function CreateDistributionStep({
  drafts,
  onDraftChange,
}: {
  drafts: AdminAppMemberScopeDrafts
  onDraftChange: (companyId: string, draft: AdminAppMemberScopeDraft) => void
}) {
  return (
    <AdminAppMembersDrawer
      app={null}
      embedded
      embeddedDrafts={drafts}
      onDraftChange={onDraftChange}
      onOpenChange={() => undefined}
      open
    />
  )
}

function CreateLifecycleStep({
  distributionMode,
  onDistributionModeChange,
  onTargetStatusChange,
  targetStatus,
}: {
  distributionMode: FormValues['distributionMode']
  onDistributionModeChange: (mode: FormValues['distributionMode']) => void
  onTargetStatusChange: (status: CreationTargetStatus) => void
  targetStatus: CreationTargetStatus
}) {
  const { t } = useTranslation('adminApps')

  return (
    <div className="grid gap-6">
      <DistributionModePicker
        compact
        mode={distributionMode}
        onModeChange={onDistributionModeChange}
      />
      <section className="grid gap-3 border-t border-[var(--color-divider)] pt-5">
        <div>
          <h4 className="text-sm font-semibold text-[var(--color-text)]">
            {t('drawer.wizard.lifecycle.targetLabel')}
          </h4>
          <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
            {t('drawer.wizard.lifecycle.targetDescription')}
          </p>
        </div>
        <div
          aria-label={t('drawer.wizard.lifecycle.targetLabel')}
          className="grid gap-3 sm:grid-cols-2"
          role="radiogroup"
        >
          {creationTargetStatuses.map((status) => {
            const selected = targetStatus === status

            return (
              <SelectableCard
                as="button"
                aria-checked={selected}
                className="cursor-pointer px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                key={status}
                onClick={() => onTargetStatusChange(status)}
                role="radio"
                selected={selected}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-[var(--color-text)]">
                      {t(`status.${status}`)}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                      {t(`drawer.wizard.lifecycle.options.${status}`)}
                    </span>
                  </span>
                  <SelectableCardIndicator selected={selected} />
                </span>
              </SelectableCard>
            )
          })}
        </div>
      </section>
    </div>
  )
}

function FieldHelp({ label }: { label: string }) {
  return (
    <HelpTooltip label={label} widthClassName="w-[220px]">
      {label}
    </HelpTooltip>
  )
}

function AdpAppValidationButton({
  disabled = false,
  isLoading,
  onClick,
}: {
  disabled?: boolean
  isLoading: boolean
  onClick: () => void
}) {
  const { t } = useTranslation('adminApps')

  return (
    <Button
      disabled={disabled || isLoading}
      isLoading={isLoading}
      onClick={onClick}
      size="sm"
      variant="soft"
    >
      {t('drawer.adpValidation.testApp')}
    </Button>
  )
}

function DrawerLoading() {
  return (
    <div className="space-y-4">
      <div className="mx-auto size-[104px] animate-pulse rounded-full bg-[var(--color-skeleton)]" />
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            className="h-[62px] animate-pulse rounded-md bg-[var(--color-skeleton)]"
            key={index}
          />
        ))}
      </div>
    </div>
  )
}

function EditableTagSelector({
  emptyText,
  id,
  onChange,
  options,
  placeholder,
  value,
}: {
  emptyText: string
  id: string
  onChange: (value: { newTags: string[]; tagIds: string[] }) => void
  options: AdminAppTag[]
  placeholder: string
  value: { newTags: string[]; tagIds: string[] }
}) {
  const { t } = useTranslation('adminApps')
  const listboxId = useId()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const selectedIds = useMemo(() => new Set(value.tagIds), [value.tagIds])
  const normalizedDraft = normalizeTagName(draft)
  const normalizedDraftKey = normalizedDraft.toLowerCase()
  const normalizedNewTagKeys = new Set(value.newTags.map((tagName) => tagName.toLowerCase()))
  const selectedExistingTags = value.tagIds
    .map((tagId) => options.find((option) => option.id === tagId))
    .filter((option): option is AdminAppTag => Boolean(option))
  const exactExistingMatch = options.find(
    (option) => option.name.toLowerCase() === normalizedDraftKey,
  )
  const canCreateDraft =
    normalizedDraft.length > 0 &&
    !exactExistingMatch &&
    !normalizedNewTagKeys.has(normalizedDraftKey)
  const filteredOptions = options.filter((option) => {
    if (selectedIds.has(option.id)) {
      return false
    }

    if (!normalizedDraftKey) {
      return true
    }

    return option.name.toLowerCase().includes(normalizedDraftKey)
  })
  const hasSelectedTags = selectedExistingTags.length > 0 || value.newTags.length > 0

  useEffect(() => {
    if (!open) {
      return undefined
    }

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [open])

  function emit(nextValue: { newTags?: string[]; tagIds?: string[] }) {
    onChange({
      newTags: nextValue.newTags ?? value.newTags,
      tagIds: nextValue.tagIds ?? value.tagIds,
    })
  }

  function toggleExistingTag(tagId: string) {
    if (selectedIds.has(tagId)) {
      emit({ tagIds: value.tagIds.filter((item) => item !== tagId) })
    } else {
      emit({ tagIds: [...value.tagIds, tagId] })
    }

    setDraft('')
    setOpen(false)
  }

  function removeExistingTag(tagId: string) {
    emit({ tagIds: value.tagIds.filter((item) => item !== tagId) })
  }

  function removeNewTag(tagName: string) {
    emit({ newTags: value.newTags.filter((item) => item !== tagName) })
  }

  function commitDraft() {
    if (!normalizedDraft) {
      return
    }

    if (exactExistingMatch) {
      if (!selectedIds.has(exactExistingMatch.id)) {
        emit({ tagIds: [...value.tagIds, exactExistingMatch.id] })
      }
      setDraft('')
      setOpen(false)
      return
    }

    if (!normalizedNewTagKeys.has(normalizedDraftKey)) {
      emit({ newTags: [...value.newTags, normalizedDraft] })
    }
    setDraft('')
    setOpen(false)
  }

  return (
    <div className="relative w-full text-sm" ref={rootRef}>
      <div
        className={cn(
          'flex min-h-[38px] w-full min-w-0 flex-wrap items-center gap-1.5 rounded-lg border border-[var(--color-control-border)] bg-white px-2 py-1.5 text-[var(--color-text)] shadow-[var(--shadow-control)] transition-colors',
          'hover:border-[var(--color-control-border-hover)] focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary-soft)]',
        )}
        onClick={() => {
          setOpen(true)
          inputRef.current?.focus()
        }}
      >
        {selectedExistingTags.map((tag) => (
          <SelectedTagChip
            key={tag.id}
            label={tag.name}
            onRemove={() => removeExistingTag(tag.id)}
            removeLabel={t('drawer.tags.remove', { name: tag.name })}
          />
        ))}
        {value.newTags.map((tagName) => (
          <SelectedTagChip
            isNew
            key={tagName}
            label={tagName}
            onRemove={() => removeNewTag(tagName)}
            removeLabel={t('drawer.tags.remove', { name: tagName })}
          />
        ))}
        <input
          aria-controls={open ? listboxId : undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="h-6 min-w-[140px] flex-1 bg-transparent text-sm text-[var(--color-text)] outline-none placeholder:text-[var(--color-control-placeholder)]"
          id={id}
          onChange={(event) => {
            setDraft(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitDraft()
              return
            }

            if (event.key === 'Escape') {
              setOpen(false)
            }
          }}
          placeholder={hasSelectedTags ? '' : placeholder}
          ref={inputRef}
          role="combobox"
          type="text"
          value={draft}
        />
      </div>
      {open ? (
        <div
          className="absolute z-[95] mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[var(--color-panel-border)] bg-white p-1 shadow-[0_8px_24px_rgba(26,28,25,0.12)]"
          id={listboxId}
          role="listbox"
        >
          {canCreateDraft ? (
            <button
              className="flex min-h-8 w-full cursor-pointer items-center rounded-md px-2 text-left text-sm font-medium text-[var(--color-primary)] hover:bg-[rgba(25,91,255,0.06)]"
              onClick={commitDraft}
              onMouseDown={(event) => event.preventDefault()}
              role="option"
              type="button"
            >
              {t('drawer.tags.createOption', { name: normalizedDraft })}
            </button>
          ) : null}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <label
                className="flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-[var(--color-text)] hover:bg-[rgba(25,91,255,0.06)]"
                key={option.id}
                role="option"
              >
                <input
                  checked={selectedIds.has(option.id)}
                  className="size-4 rounded border-[#d7d8da] accent-[var(--color-primary)]"
                  onChange={() => toggleExistingTag(option.id)}
                  type="checkbox"
                />
                <span className="min-w-0 truncate">{option.name}</span>
              </label>
            ))
          ) : canCreateDraft ? null : (
            <div className="px-2 py-1.5 text-sm text-[var(--color-text-muted)]">
              {normalizedDraft ? t('drawer.tags.noMatches') : emptyText}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function SelectedTagChip({
  isNew = false,
  label,
  onRemove,
  removeLabel,
}: {
  isNew?: boolean
  label: string
  onRemove: () => void
  removeLabel: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 max-w-full items-center gap-1 rounded-full border px-2 text-xs leading-none',
        isNew
          ? 'border-dashed border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
          : 'border-[var(--color-panel-border)] bg-[var(--color-hover-muted)] text-[rgba(26,28,25,0.74)]',
      )}
      title={label}
    >
      <span className="min-w-0 truncate">{label}</span>
      <button
        aria-label={removeLabel}
        className="inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-current opacity-70 hover:bg-black/10 hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus)]"
        onClick={(event) => {
          event.stopPropagation()
          onRemove()
        }}
        type="button"
      >
        <svg aria-hidden className="size-3" fill="none" viewBox="0 0 12 12">
          <path
            d="m3.5 3.5 5 5m0-5-5 5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6"
          />
        </svg>
      </button>
    </span>
  )
}

function AppConfigSection({
  adpCredentials,
  errors,
  fields,
  framed = true,
  layout = true,
  onConfigChange,
  registeredModels,
  values,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  errors: FormErrors
  fields: AdminAppTypeConfigField[]
  framed?: boolean
  layout?: boolean
  onConfigChange: (key: string, value: AppConfigValue) => void
  registeredModels: RegisteredModelSelectState
  values: Record<string, AppConfigValue>
}) {
  const { t } = useTranslation('adminApps')
  const switchFields = fields.filter((field) => field.field_type === 'checkbox')
  const inputFields = fields.filter((field) => field.field_type !== 'checkbox')

  const content = (
    <>
      <DrawerFormRow className="gap-5" columns={1}>
        {inputFields.map((field) => (
          <ConfigField
            error={errors[field.key]}
            field={field}
            key={field.key}
            adpCredentials={adpCredentials}
            configValues={values}
            maskedValue={field.is_sensitive ? maskedFieldValue(values, field) : ''}
            onConfigChange={onConfigChange}
            registeredModels={registeredModels}
            value={values[field.key]}
          />
        ))}
      </DrawerFormRow>
      {switchFields.length > 0 ? (
        <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
          {switchFields.map((field) =>
            field.key === 'show_tool_invocation_details' ? (
              <div
                className="flex w-full items-start justify-between gap-4 rounded-lg border border-[var(--color-panel-border)] bg-[rgba(26,28,25,0.015)] px-4 py-3"
                key={field.key}
              >
                <div>
                  <div className="text-sm font-medium text-[var(--color-text)]">
                    {translatedConfigFieldLabel(t, field)}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
                    {t('drawer.configFieldHints.show_tool_invocation_details')}
                  </p>
                </div>
                <Switch
                  aria-label={translatedConfigFieldLabel(t, field)}
                  checked={configValueToBoolean(values[field.key])}
                  onCheckedChange={(checked) => onConfigChange(field.key, checked)}
                />
              </div>
            ) : (
              <label
                className="inline-flex min-h-6 cursor-pointer items-center gap-2 text-sm font-normal text-[#374151]"
                key={field.key}
              >
                <input
                  checked={configValueToBoolean(values[field.key])}
                  className="size-4 rounded border-[#d7d8da] text-[var(--color-primary)] accent-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                  onChange={(event) => onConfigChange(field.key, event.target.checked)}
                  type="checkbox"
                />
                <span>{translatedConfigFieldLabel(t, field)}</span>
              </label>
            ),
          )}
        </div>
      ) : null}
    </>
  )

  if (!framed) {
    return layout ? <DrawerFormLayout className="mt-0">{content}</DrawerFormLayout> : content
  }

  return (
    <FormSection
      className="space-y-4 bg-[rgba(26,28,25,0.015)]"
      title={t('drawer.fields.appConfig')}
    >
      {content}
    </FormSection>
  )
}

function AppConfigSectionSkeleton({ framed = true }: { framed?: boolean }) {
  const { t } = useTranslation('adminApps')
  const skeleton = (
    <div className="grid gap-4">
      <div className="h-[62px] animate-pulse rounded-md bg-[var(--color-skeleton)]" />
      <div className="h-[62px] animate-pulse rounded-md bg-[var(--color-skeleton)]" />
    </div>
  )

  if (!framed) {
    return skeleton
  }

  return (
    <FormSection
      className="space-y-4 bg-[rgba(26,28,25,0.015)]"
      title={t('drawer.fields.appConfig')}
    >
      {skeleton}
    </FormSection>
  )
}

function ConfigField({
  adpCredentials,
  configValues,
  error,
  field,
  maskedValue = '',
  onConfigChange,
  registeredModels,
  value,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  configValues: Record<string, AppConfigValue>
  error?: string
  field: AdminAppTypeConfigField
  maskedValue?: string
  onConfigChange: (key: string, value: AppConfigValue) => void
  registeredModels: RegisteredModelSelectState
  value: AppConfigValue | undefined
}) {
  const { t } = useTranslation('adminApps')
  const id = `admin-app-config-${field.key}`
  const inputValue = configValueToString(value)
  const label = translatedConfigFieldLabel(t, field)

  if (field.key === 'registered_model_id') {
    return (
      <RegisteredModelConfigSelect
        configValues={configValues}
        error={error}
        field={field}
        onConfigChange={onConfigChange}
        registeredModels={registeredModels}
        value={value}
      />
    )
  }

  if (field.field_type === 'adp_credential_select') {
    return (
      <TencentAdpCredentialConfigSelect
        adpCredentials={adpCredentials}
        error={error}
        field={field}
        onConfigChange={onConfigChange}
        value={value}
      />
    )
  }

  return (
    <FormField
      error={error}
      hint={field.is_sensitive && maskedValue ? t('drawer.secretConfigured') : undefined}
      htmlFor={id}
      label={label}
      required={field.required}
    >
      {field.is_sensitive ? (
        <PasswordInput
          hideLabel={t('drawer.hideSecret')}
          id={id}
          invalid={Boolean(error)}
          onChange={(event) => onConfigChange(field.key, event.target.value)}
          placeholder={
            maskedValue
              ? t('drawer.placeholders.replaceSecret')
              : field.placeholder || t('drawer.placeholders.secret')
          }
          showLabel={t('drawer.showSecret')}
          showVisibilityToggle={inputValue.length > 0}
          value={inputValue}
        />
      ) : field.field_type === 'textarea' ? (
        <Textarea
          id={id}
          invalid={Boolean(error)}
          onChange={(event) => onConfigChange(field.key, event.target.value)}
          placeholder={field.placeholder}
          value={inputValue}
        />
      ) : (
        <Input
          id={id}
          invalid={Boolean(error)}
          onChange={(event) => onConfigChange(field.key, event.target.value)}
          placeholder={field.placeholder}
          value={inputValue}
        />
      )}
    </FormField>
  )
}

function TencentAdpCredentialConfigSelect({
  adpCredentials,
  error,
  field,
  onConfigChange,
  value,
}: {
  adpCredentials: TencentAdpCredentialSelectState
  error?: string
  field: AdminAppTypeConfigField
  onConfigChange: (key: string, value: AppConfigValue) => void
  value: AppConfigValue | undefined
}) {
  const { t } = useTranslation('adminApps')
  const id = `admin-app-config-${field.key}`
  const selectedCredentialId = configValueToString(value)
  const selectError = adpCredentials.isError ? t('drawer.adpCredentials.loadFailed') : error
  const options = useMemo<SelectOption[]>(() => {
    const placeholderLabel = adpCredentials.isLoading
      ? t('drawer.adpCredentials.loading')
      : adpCredentials.isError
        ? t('drawer.adpCredentials.loadFailed')
        : adpCredentials.credentials.length === 0
          ? t('drawer.adpCredentials.empty')
          : t('drawer.adpCredentials.placeholder')
    const credentialOptions: SelectOption[] = adpCredentials.credentials.map((credential) => ({
      label: adpCredentialOptionLabel(credential),
      value: credential.id,
    }))

    if (
      selectedCredentialId &&
      !credentialOptions.some((option) => option.value === selectedCredentialId)
    ) {
      credentialOptions.push({
        label: t('drawer.adpCredentials.unavailable'),
        value: selectedCredentialId,
      })
    }

    return [{ label: placeholderLabel, value: '' }, ...credentialOptions]
  }, [
    adpCredentials.credentials,
    adpCredentials.isError,
    adpCredentials.isLoading,
    selectedCredentialId,
    t,
  ])

  return (
    <FormField
      error={selectError}
      htmlFor={id}
      labelExtra={
        <a
          className="ml-2 text-xs font-medium text-[var(--color-primary)] hover:text-[var(--color-primary-hover)]"
          href="/admin/credentials/adp"
          rel="noreferrer"
          target="_blank"
        >
          {t('drawer.adpCredentials.manageLink')}
        </a>
      }
      label={translatedConfigFieldLabel(t, field)}
      required={field.required}
    >
      <Select
        aria-label={translatedConfigFieldLabel(t, field)}
        disabled={adpCredentials.isLoading}
        id={id}
        invalid={Boolean(selectError)}
        onClick={adpCredentials.refresh}
        onValueChange={(nextCredentialId) => onConfigChange(field.key, nextCredentialId)}
        options={options}
        value={selectedCredentialId}
      />
    </FormField>
  )
}

function RegisteredModelConfigSelect({
  configValues,
  error,
  field,
  onConfigChange,
  registeredModels,
  value,
}: {
  configValues: Record<string, AppConfigValue>
  error?: string
  field: AdminAppTypeConfigField
  onConfigChange: (key: string, value: AppConfigValue) => void
  registeredModels: RegisteredModelSelectState
  value: AppConfigValue | undefined
}) {
  const { t } = useTranslation('adminApps')
  const id = `admin-app-config-${field.key}`
  const selectedModelId = configValueToString(value)
  const savedModelSnapshot = configValueToString(configValues.registered_model_name_snapshot)
  const selectedModel = registeredModels.models.find((model) => model.id === selectedModelId)
  const selectError = registeredModels.isError ? t('drawer.registeredModels.loadFailed') : error
  const options = useMemo<SelectOption[]>(() => {
    const placeholderLabel = registeredModels.isLoading
      ? t('drawer.registeredModels.loading')
      : registeredModels.isError
        ? t('drawer.registeredModels.loadFailed')
        : registeredModels.models.length === 0
          ? t('drawer.registeredModels.empty')
          : t('drawer.registeredModels.placeholder')
    const modelOptions: SelectOption[] = registeredModels.models.map((model) => ({
      icon: (
        <ProviderLogo
          className="size-5 rounded-md p-1"
          label={registeredModelProviderLabel(model)}
          provider={model.provider}
          variant="compact"
        />
      ),
      label: registeredModelOptionLabel(model),
      value: model.id,
    }))

    if (selectedModelId && !modelOptions.some((option) => option.value === selectedModelId)) {
      modelOptions.push({
        label: savedModelSnapshot || t('drawer.registeredModels.unavailable'),
        value: selectedModelId,
      })
    }

    return [{ label: placeholderLabel, value: '' }, ...modelOptions]
  }, [
    registeredModels.isError,
    registeredModels.isLoading,
    registeredModels.models,
    savedModelSnapshot,
    selectedModelId,
    t,
  ])

  function handleModelChange(nextModelId: string) {
    const nextModel = registeredModels.models.find((model) => model.id === nextModelId)

    onConfigChange(field.key, nextModelId)
    onConfigChange(
      'registered_model_name_snapshot',
      nextModel ? registeredModelDisplayName(nextModel) : '',
    )
  }

  return (
    <FormField
      error={selectError}
      htmlFor={id}
      label={translatedConfigFieldLabel(t, field)}
      required={field.required}
    >
      <Select
        aria-label={translatedConfigFieldLabel(t, field)}
        disabled={
          registeredModels.isLoading ||
          registeredModels.isError ||
          (registeredModels.models.length === 0 && !selectedModel)
        }
        id={id}
        invalid={Boolean(selectError)}
        onValueChange={handleModelChange}
        options={options}
        value={selectedModelId}
      />
    </FormField>
  )
}
